import React, { useEffect, useState, useCallback } from 'react';
import {
  Smartphone,
  Save,
  CheckCircle,
  AlertCircle,
  Plus,
  X,
  ExternalLink,
  RefreshCw,
  ShieldAlert,
  BellRing,
  UserCheck,
  KeyRound,
} from 'lucide-react';
import api from '../../services/api';
import './AppVersionManagement.css';

const PLATFORM_META = {
  android: {
    label: 'Android',
    icon: '🤖',
    placeholder: 'https://play.google.com/store/apps/details?id=...',
    color: 'bg-green-50 border-green-200',
    badge: 'bg-green-100 text-green-700',
  },
  ios: {
    label: 'iOS',
    icon: '🍎',
    placeholder: 'https://apps.apple.com/in/app/...',
    color: 'bg-blue-50 border-blue-200',
    badge: 'bg-blue-100 text-blue-700',
  },
};

const defaultForm = () => ({
  latestVersion: '',
  allowedVersions: [],
  storeUrl: '',
  isForce: true,
  whatsNew: '',
  isEnabled: true,
  demoEnabled: true,
  demoPhones: [],
  demoOtp: '123456',
});

function PhoneTagInput({ phones = [], onChange }) {
  const [input, setInput] = useState('');

  const add = () => {
    const p = input.replace(/\D/g, '').slice(-10);
    if (p.length === 10 && !phones.includes(p)) {
      onChange([...phones, p]);
      setInput('');
    }
  };

  const remove = (p) => onChange(phones.filter((x) => x !== p));

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); add(); }
          }}
          placeholder="10-digit mobile, e.g. 9999999999"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E] bg-white"
        />
        <button
          type="button"
          onClick={add}
          className="flex items-center gap-1 bg-[#548B2E] hover:bg-[#3f6b20] text-white px-3 py-2 rounded-lg text-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Phone
        </button>
      </div>
      <div className="flex flex-wrap gap-2 min-h-[32px]">
        {phones.length === 0 && (
          <span className="text-xs text-gray-400 italic">No demo phone numbers added yet</span>
        )}
        {phones.map((p) => (
          <span
            key={p}
            className="inline-flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-800 text-xs px-2.5 py-1 rounded-full font-mono font-medium"
          >
            📱 +91 {p}
            <button type="button" onClick={() => remove(p)} className="hover:text-red-500 transition-colors">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

function VersionTagInput({ versions, onChange }) {
  const [input, setInput] = useState('');

  const add = () => {
    const v = input.trim();
    if (v && !versions.includes(v)) {
      onChange([...versions, v]);
    }
    setInput('');
  };

  const remove = (v) => onChange(versions.filter((x) => x !== v));

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); add(); }
          }}
          placeholder="e.g. 3.6.33"
          className="version-tag-input-field"
        />
        <button
          type="button"
          onClick={add}
          className="flex items-center gap-1 bg-[#548B2E] hover:bg-[#3f6b20] text-white px-3 py-2 rounded-lg text-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      </div>
      <div className="flex flex-wrap gap-2 min-h-[32px]">
        {versions.length === 0 && (
          <span className="text-xs text-gray-400 italic">No versions added yet</span>
        )}
        {versions.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 text-xs px-2.5 py-1 rounded-full font-mono"
          >
            {v}
            <button type="button" onClick={() => remove(v)} className="hover:text-red-500 transition-colors">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

function DemoAccountsCard({ initialData, onSaved }) {
  const [enabled, setEnabled] = useState(true);
  const [phones, setPhones] = useState([]);
  const [otp, setOtp] = useState('123456');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (initialData) {
      if (initialData.demoEnabled !== undefined) setEnabled(initialData.demoEnabled);
      if (initialData.demoPhones) setPhones(initialData.demoPhones);
      if (initialData.demoOtp) setOtp(initialData.demoOtp);
    }
  }, [initialData]);

  const handleSaveWithState = async (newEnabled, newPhones, newOtp) => {
    setSaving(true);
    setMessage(null);
    try {
      // Fetch latest platform configs from backend to ensure we have valid required fields
      const res = await api.get('/admin/app-version/config');
      const allConfigs = res.data || [];
      if (allConfigs.length === 0) {
        throw new Error('No platform configs found');
      }

      await Promise.all(allConfigs.map(async (cfg) => {
        await api.put('/admin/app-version/config', {
          platform: cfg.platform,
          latestVersion: cfg.latestVersion ?? cfg.latest_version,
          allowedVersions: cfg.allowedVersions ?? cfg.allowed_versions ?? [],
          storeUrl: cfg.storeUrl ?? cfg.store_url,
          isForce: (cfg.isForce ?? cfg.is_force) !== false,
          whatsNew: cfg.whatsNew ?? cfg.whats_new ?? null,
          isEnabled: (cfg.isEnabled ?? cfg.is_enabled) !== false,
          demoEnabled: newEnabled !== undefined ? newEnabled : enabled,
          demoPhones: newPhones || phones,
          demoOtp: (newOtp !== undefined ? newOtp : otp).trim() || '123456',
        });
      }));
      setMessage({ type: 'success', text: newEnabled !== undefined ? `Demo bypass ${newEnabled ? 'enabled' : 'disabled'} successfully!` : 'Reviewer demo settings saved successfully!' });
      if (onSaved) onSaved();
    } catch {
      setMessage({ type: 'error', text: 'Failed to save demo settings.' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = () => {
    const next = !enabled;
    setEnabled(next);
    handleSaveWithState(next, phones, otp);
  };

  const handleSave = async () => {
    await handleSaveWithState(enabled, phones, otp);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
            <UserCheck className="w-5 h-5 text-[#548B2E]" />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-800">Store Reviewer &amp; Demo Account Bypass</h2>
            <p className="text-xs text-gray-500">
              Shared demo credentials for Apple App Store &amp; Google Play Store review submissions
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-600">
            {enabled ? 'Active' : 'Disabled'}
          </span>
          <div
            onClick={handleToggle}
            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
              enabled ? 'bg-[#548B2E]' : 'bg-gray-300'
            }`}
          >
            <div
              className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                enabled ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </div>
        </div>
      </div>

      {message && (
        <div
          className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg text-xs ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {enabled && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3 border-t border-gray-100">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Reviewer Demo Phone Numbers
            </label>
            <PhoneTagInput phones={phones} onChange={setPhones} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Fixed Demo OTP Code
            </label>
            <div className="relative">
              <KeyRound className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="123456"
                maxLength={10}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#548B2E] bg-white"
              />
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="mt-3 w-full flex items-center justify-center gap-1.5 bg-[#548B2E] hover:bg-[#3f6b20] disabled:opacity-60 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? 'Saving...' : 'Save Reviewer Settings'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PlatformCard({ platform, initialData, onSaved }) {
  const meta = PLATFORM_META[platform];
  const [form, setForm] = useState(() => ({
    ...defaultForm(),
    ...(initialData || {}),
  }));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (initialData) setForm({ ...defaultForm(), ...initialData });
  }, [initialData]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.latestVersion.trim()) {
      setMessage({ type: 'error', text: 'Latest version is required.' });
      return;
    }
    if (!form.storeUrl.trim()) {
      setMessage({ type: 'error', text: 'Store URL is required.' });
      return;
    }
    if (form.allowedVersions.length === 0) {
      setMessage({ type: 'error', text: 'Add at least one allowed version.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await api.put('/admin/app-version/config', {
        platform,
        latestVersion: form.latestVersion.trim(),
        allowedVersions: form.allowedVersions,
        storeUrl: form.storeUrl.trim(),
        isForce: form.isForce,
        whatsNew: form.whatsNew?.trim() || null,
        isEnabled: form.isEnabled,
        demoEnabled: form.demoEnabled,
        demoPhones: form.demoPhones,
        demoOtp: form.demoOtp,
      });
      setMessage({ type: 'success', text: 'Config saved successfully!' });
      if (onSaved) onSaved();
    } catch {
      setMessage({ type: 'error', text: 'Failed to save. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`border rounded-xl p-6 ${meta.color}`}>
      <div className="flex items-center gap-3 mb-5">
        <span className="text-2xl">{meta.icon}</span>
        <div>
          <h2 className="platform-card-title">{meta.label} Config</h2>
          <p className="text-xs text-gray-500">Play Store / App Store version control</p>
        </div>
        {/* Enabled toggle pill */}
        <div className="ml-auto">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <span className="text-xs font-medium text-gray-600">
              {form.isEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <div
              onClick={() => set('isEnabled', !form.isEnabled)}
              className={`relative w-10 h-5 rounded-full transition-colors ${
                form.isEnabled ? 'bg-[#548B2E]' : 'bg-gray-300'
              }`}
            >
              <div
                className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  form.isEnabled ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </div>
          </label>
        </div>
      </div>

      {message && (
        <div
          className={`flex items-center gap-2 mb-4 px-4 py-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {message.type === 'success'
            ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
            : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
          {message.text}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-4">
        {/* Latest version */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Latest Version <span className="text-red-500">*</span>
            <span className="ml-1 text-xs text-gray-400 font-normal">— semver format, e.g. 3.6.33 or 3.6.33+103</span>
          </label>
          <input
            type="text"
            value={form.latestVersion}
            onChange={(e) => set('latestVersion', e.target.value)}
            placeholder="e.g. 3.6.33"
            maxLength={20}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E] bg-white"
          />
        </div>

        {/* Allowed versions */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Allowed Versions <span className="text-red-500">*</span>
            <span className="ml-1 text-xs text-gray-400 font-normal">— users on these versions will NOT be prompted</span>
          </label>
          <VersionTagInput
            versions={form.allowedVersions}
            onChange={(v) => set('allowedVersions', v)}
          />
        </div>

        {/* Store URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Store URL <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <input
              type="url"
              value={form.storeUrl}
              onChange={(e) => set('storeUrl', e.target.value)}
              placeholder={meta.placeholder}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E] bg-white"
            />
            {form.storeUrl && (
              <a
                href={form.storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-10 h-10 border border-gray-300 rounded-lg hover:bg-white transition-colors"
                title="Test link"
              >
                <ExternalLink className="w-4 h-4 text-gray-500" />
              </a>
            )}
          </div>
        </div>

        {/* Force mode toggle */}
        <div className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-gray-200">
          <div className="flex items-center gap-3">
            <ShieldAlert className={`w-5 h-5 ${form.isForce ? 'text-red-500' : 'text-gray-400'}`} />
            <div>
              <p className="text-sm font-medium text-gray-800">
                {form.isForce ? 'Force Update (cannot dismiss)' : 'Soft Update (can dismiss)'}
              </p>
              <p className="text-xs text-gray-500">
                {form.isForce
                  ? 'Users must update before continuing'
                  : 'Users can tap "Later" to continue'}
              </p>
            </div>
          </div>
          <div
            onClick={() => set('isForce', !form.isForce)}
            className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${
              form.isForce ? 'bg-red-500' : 'bg-gray-300'
            }`}
          >
            <div
              className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                form.isForce ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </div>
        </div>

        {/* What's new */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            What's New <span className="text-gray-400 text-xs font-normal">(optional)</span>
          </label>
          <textarea
            value={form.whatsNew}
            onChange={(e) => set('whatsNew', e.target.value)}
            placeholder="e.g. Bug fixes and performance improvements. New checkout experience."
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E] bg-white resize-none"
          />
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 bg-[#548B2E] hover:bg-[#3f6b20] disabled:opacity-60 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : `Save ${meta.label} Config`}
        </button>
      </form>
    </div>
  );
}

export default function AppVersionManagement() {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/app-version/config');
      const map = {};
      let demoCfg = null;
      (res.data || []).forEach((c) => {
        const isForceVal = c.isForce ?? c.is_force;
        const isEnabledVal = c.isEnabled ?? c.is_enabled;
        const isDemoEnabledVal = c.demoEnabled ?? c.demo_enabled;
        map[c.platform] = {
          latestVersion: c.latestVersion ?? c.latest_version ?? '',
          allowedVersions: c.allowedVersions ?? c.allowed_versions ?? [],
          storeUrl: c.storeUrl ?? c.store_url ?? '',
          isForce: isForceVal === true || isForceVal === 1 || isForceVal === 'true' || isForceVal === '1' || isForceVal === undefined,
          whatsNew: c.whatsNew ?? c.whats_new ?? '',
          isEnabled: isEnabledVal === true || isEnabledVal === 1 || isEnabledVal === 'true' || isEnabledVal === '1' || isEnabledVal === undefined,
          demoEnabled: isDemoEnabledVal === true || isDemoEnabledVal === 1 || isDemoEnabledVal === 'true' || isDemoEnabledVal === '1' || isDemoEnabledVal === undefined,
          demoPhones: c.demoPhones ?? c.demo_phones ?? [],
          demoOtp: c.demoOtp ?? c.demo_otp ?? '123456',
        };
        if (!demoCfg && (c.demoPhones || c.demoOtp !== undefined)) {
          demoCfg = {
            demoEnabled: map[c.platform].demoEnabled,
            demoPhones: map[c.platform].demoPhones,
            demoOtp: map[c.platform].demoOtp,
          };
        }
      });
      map._demo = demoCfg || {
        demoEnabled: true,
        demoPhones: [],
        demoOtp: '123456',
      };
      setConfigs(map);
    } catch {
      setError('Failed to load version configs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#548B2E] rounded-lg flex items-center justify-center">
            <Smartphone className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">App Version Management</h1>
            <p className="text-sm text-gray-500">Control force/soft update prompts and store reviewer accounts</p>
          </div>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-2 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6">
        <BellRing className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-700">
          The mobile app checks version rules on launch and app resume.
          Store reviewer numbers bypass SMS delivery across both Android and iOS submissions.
        </p>
      </div>

      {/* Single Common Demo Accounts Card */}
      {!loading && !error && (
        <DemoAccountsCard initialData={configs._demo} onSaved={load} />
      )}

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {['android', 'ios'].map((p) => (
            <div key={p} className="h-96 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-4 text-red-700 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
          <button onClick={load} className="ml-auto underline">Retry</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {['android', 'ios'].map((platform) => (
            <PlatformCard
              key={platform}
              platform={platform}
              initialData={{...configs[platform], ...configs._demo}}
              onSaved={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}
